import { clearUser } from "@store/auth/userSlice";
import { setDistributorsData } from "@store/modules/accountManagement/distributorsSlice";
import axios from "axios";

const handleUnauthorized = (dispatch, navigate) => {
	if (dispatch && navigate) {
		dispatch(clearUser());
		navigate("/login");
	} else {
		console.error("Dispatch or navigate function is missing.");
	}
};

export const fetchDistributersData = async (
	token,
	dispatch,
	navigate,
	page = 1,
	perPage = 10,
	paginate = 1,
	searchTerms,
	store = true,
	signal,
) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		params: {
			page: page,
			per_page: perPage,
			businessUnits: searchTerms?.businessUnits || null,
			name: searchTerms?.name || null,
			paginate: paginate,
			type: searchTerms?.type || null,
		},
		signal: signal,
	};

	try {
		const response = await axios.get(`${import.meta.env.VITE_API_BACKEND}api/companies`, config);

		let data;
		if (!response.data || response.data.length === 0) {
			data = {
				data: [],
				totalItems: 0,
				page: paginate === 1 ? page : 1,
				perPage: paginate === 1 ? perPage : 10,
			};
		} else if (paginate === 1) {
			const totalItems = response.data?.total;
			data = {
				data: response.data.data,
				totalItems: totalItems,
				page: paginate === 1 ? page : 1,
				perPage: paginate === 1 ? perPage : 10,
			};
		} else {
			data = {
				data: response.data,
				totalItems: 0,
				page: 1,
				perPage: 10,
			};
		}
		store && dispatch(setDistributorsData(data));
		return data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching Distributers data:", error);
		}
	}
};

export const createDistributor = async (formData, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "multipart/form-data",
		},
	};

	try {
		const response = await axios.post(
			`${import.meta.env.VITE_API_BACKEND}api/companies`,
			formData,
			config,
		);
		return { status: response.status, data: response.data };
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else if (error.response && error.response.status === 400) {
			const serverMessage = error.response.data.message || "Unexpected error.";
			return { errors: serverMessage };
		} else if (error.response && error.response.status === 404) {
			const serverMessage = error.response.data.message || "Unexpected error.";
			return { errors: serverMessage };
		} else if (error.response && error.response.status === 422) {
			return { errors: error.response.data.errors };
		} else {
			console.error("Error creating distributor:", error);
		}
		return null;
	}
};

export const updateDistributors = async (id, formData, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "multipart/form-data",
		},
	};
	try {
		const response = await axios.post(
			`${import.meta.env.VITE_API_BACKEND}api/companies/${id}`,
			formData,
			config,
		);
		return { status: response.status, data: response.data };
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else if (error.response && error.response.status === 400) {
			const serverMessage = error.response.data.message || "Unexpected error.";
			return { errors: serverMessage };
		} else if (error.response && error.response.status === 404) {
			const serverMessage = error.response.data.message || "Unexpected error.";
			return { errors: serverMessage };
		} else if (error.response && error.response.status === 422) {
			return { errors: error.response.data.errors };
		} else {
			console.error("Error updating distributor:", error);
		}
		return null;
	}
};

export const deleteDistributorById = async (id, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};
	try {
		const response = await axios.delete(
			`${import.meta.env.VITE_API_BACKEND}api/companies/${id}`,
			config,
		);
		if (response.status === 200 || response.status === 202 || response.status === 204) {
			return {
				status: "success",
				message: "Distributor deleted successfully.",
			};
		} else {
			return {
				status: "error",
				message: response.data?.message || "An unexpected error occurred.",
			};
		}
	} catch (error) {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					handleUnauthorized(dispatch, navigate);
					break;
				case 404:
					return { status: "not_found", message: "Not found." };
				case 405:
					return {
						status: "method_not_allowed",
						message: "Method Not Allowed",
					};
				case 400:
					return {
						status: "error",
						message: error.response.data?.message || "An error occurred.",
					};
				default:
					return { status: "error", message: "An error occurred." };
			}
		} else {
			return { status: "error", message: "Network error" };
		}
	}
};

export const getDistributorById = async (id, token) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
			Accept: "*/*",
		},
	};

	try {
		const response = await axios.get(
			`${import.meta.env.VITE_API_BACKEND}api/companies/${id}`,
			config,
		);
		return { status: response.status, data: response.data };
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching device info:", error);
			throw error;
		}
	}
};
export const getDistributorPhotoById = async (id, token) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
			Accept: "*/*",
		},
		responseType: "blob",
	};

	try {
		const response = await axios.get(
			`${import.meta.env.VITE_API_BACKEND}api/companies/${id}/download-photo`,
			config,
		);
		return { status: response.status, data: response.data };
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching device info:", error);
			throw error;
		}
	}
};
